import { Box, Button, CloseButton, Divider, FullScreenModalLayout, Heading, Modal, RadioGroup, Tag, Text } from '@wix/design-system';
import { GiftSmall } from '@wix/wix-ui-icons-common/lazy';
import classnames from 'clsx';
import React, { useRef } from 'react';
import s from './MobileFiltersModal.module.scss';
import { CoreDomainSearchModule } from '../../module/module';
import { DEFAULT_STYLES } from '../../types';
import { ClearFilters } from '../../layouts/ClearFilters';
import { WIX_HEADER_HEIGHT } from '../../constants';
const arrayChanged = (current, initial) => {
  if (current.length !== initial.length) {
    return true;
  }
  const initialSet = new Set(initial);
  return current.some(item => !initialSet.has(item));
};
export const MobileFiltersModal = _ref => {
  let isOpen = _ref.isOpen,
    onClose = _ref.onClose,
    availableTlds = _ref.availableTlds,
    concatenatedKeywords = _ref.concatenatedKeywords,
    onFiltersChange = _ref.onFiltersChange;
  const _CoreDomainSearchModu = CoreDomainSearchModule.internals.useTranslations(),
    t = _CoreDomainSearchModu.t;
  const selectedTlds = CoreDomainSearchModule.SDK.domainInputStore.useTlds();
  const selectedKeywords = CoreDomainSearchModule.SDK.domainInputStore.useKeywords();
  const businessInfo = CoreDomainSearchModule.SDK.domainInputStore.useBusinessInfo();
  const submittedDomain = CoreDomainSearchModule.SDK.domainInputStore.useSubmittedDomain();
  const selectedStyle = CoreDomainSearchModule.SDK.domainInputStore.useStyle();
  const _CoreDomainSearchModu2 = CoreDomainSearchModule.SDK.domainInputStore,
    setTlds = _CoreDomainSearchModu2.setTlds,
    setKeywords = _CoreDomainSearchModu2.setKeywords,
    setStyle = _CoreDomainSearchModu2.setStyle,
    setFilters = _CoreDomainSearchModu2.setFilters,
    setBusinessInfo = _CoreDomainSearchModu2.setBusinessInfo,
    setSubmittedDomain = _CoreDomainSearchModu2.setSubmittedDomain,
    clearFilters = _CoreDomainSearchModu2.clearFilters;
  const prevIsOpenRef = useRef(isOpen);
  const initialFilters = useRef({
    tlds: selectedTlds,
    keywords: selectedKeywords,
    style: selectedStyle,
    businessInfo,
    submittedDomain
  });
  if (!prevIsOpenRef.current && isOpen) {
    initialFilters.current = {
      tlds: selectedTlds,
      keywords: selectedKeywords,
      style: selectedStyle,
      businessInfo,
      submittedDomain
    };
  }
  prevIsOpenRef.current = isOpen;
  const shouldShowClearFilters = selectedTlds.length > 0 || selectedKeywords.length > 0 || !!selectedStyle;
  const handleToggleTld = tld => {
    setTlds(selectedTlds.includes(tld) ? selectedTlds.filter(id => id !== tld) : [...selectedTlds, tld]);
  };
  const handleToggleKeyword = keyword => {
    setKeywords(selectedKeywords.includes(keyword) ? selectedKeywords.filter(k => k !== keyword) : [...selectedKeywords, keyword]);
  };
  const tldsChanged = arrayChanged(selectedTlds, initialFilters.current.tlds);
  const keywordsChanged = arrayChanged(selectedKeywords, initialFilters.current.keywords);
  const styleChanged = selectedStyle !== initialFilters.current.style;
  const hasChanges = tldsChanged || keywordsChanged || styleChanged;
  const handleApply = () => {
    if (tldsChanged) {
      onFiltersChange == null || onFiltersChange('tlds', selectedTlds);
    }
    if (keywordsChanged) {
      onFiltersChange == null || onFiltersChange('keywords', selectedKeywords);
    }
    if (styleChanged) {
      onFiltersChange == null || onFiltersChange('style', selectedStyle ?? '');
    }
    onClose();
  };
  const handleClose = () => {
    setFilters(initialFilters.current);
    onClose();
  };
  const handleClear = () => {
    initialFilters.current = {
      businessInfo: initialFilters.current.businessInfo,
      keywords: [],
      tlds: [],
      style: undefined,
      submittedDomain: initialFilters.current.submittedDomain
    };
    clearFilters();
    setSubmittedDomain(initialFilters.current.submittedDomain);
    if (initialFilters.current.businessInfo) {
      setBusinessInfo(initialFilters.current.businessInfo);
    }
  };
  const styleOptions = DEFAULT_STYLES.map(_ref2 => {
    let id = _ref2.id,
      key = _ref2.key;
    return {
      id,
      value: t(key)
    };
  });
  return /*#__PURE__*/React.createElement(Modal, {
    isOpen: isOpen,
    onRequestClose: handleClose,
    screen: "full",
    transition: "moveVertical"
  }, /*#__PURE__*/React.createElement(FullScreenModalLayout, {
    style: {
      paddingTop: WIX_HEADER_HEIGHT
    }
  }, /*#__PURE__*/React.createElement(FullScreenModalLayout.Header, {
    startNode: shouldShowClearFilters ? /*#__PURE__*/React.createElement(ClearFilters, {
      onClear: handleClear,
      underline: true
    }) : undefined,
    endNode: /*#__PURE__*/React.createElement(CloseButton, {
      skin: "standardFilled",
      size: "medium",
      onClick: handleClose
    })
  }, /*#__PURE__*/React.createElement(React.Fragment, null)), /*#__PURE__*/React.createElement(FullScreenModalLayout.Content, {
    padding: true
  }, /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: "SP6"
  }, /*#__PURE__*/React.createElement(Heading, {
    size: "large",
    className: s.title
  }, t('core_domain_search.mobile.filters_modal.title')), /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: "SP4"
  }, /*#__PURE__*/React.createElement(Box, {
    direction: "vertical"
  }, /*#__PURE__*/React.createElement(Box, {
    paddingTop: "SP4",
    paddingBottom: "SP4"
  }, /*#__PURE__*/React.createElement(Text, {
    size: "small",
    weight: "bold"
  }, t('core_domain_search.mobile.filters_modal.domain_extensions'))), /*#__PURE__*/React.createElement(Box, {
    gap: "SP3",
    flexWrap: "wrap"
  }, availableTlds.map(_ref3 => {
    let tld = _ref3.tld,
      isVoucherSupported = _ref3.isVoucherSupported;
    const isSelected = selectedTlds.includes(tld);
    return /*#__PURE__*/React.createElement(Tag, {
      key: tld,
      id: tld,
      skin: "lightOutlined",
      removable: false,
      className: classnames(s.tagContainer, {
        [s.tagSelected]: isSelected
      }),
      onClick: () => handleToggleTld(tld)
    }, /*#__PURE__*/React.createElement(Box, {
      gap: "3px",
      direction: "horizontal",
      verticalAlign: "middle"
    }, /*#__PURE__*/React.createElement(Text, {
      size: "small"
    }, ".", tld), isVoucherSupported && /*#__PURE__*/React.createElement(GiftSmall, null)));
  }))), /*#__PURE__*/React.createElement(Box, {
    direction: "vertical"
  }, /*#__PURE__*/React.createElement(Box, {
    paddingTop: "SP4",
    paddingBottom: "SP4"
  }, /*#__PURE__*/React.createElement(Text, {
    size: "small",
    weight: "bold"
  }, t('core_domain_search.domain_gate.keywords_header'))), /*#__PURE__*/React.createElement(Box, {
    gap: "SP3",
    flexWrap: "wrap"
  }, concatenatedKeywords.map(keyword => {
    const isSelected = selectedKeywords.includes(keyword);
    return /*#__PURE__*/React.createElement(Tag, {
      key: keyword,
      id: keyword,
      skin: "lightOutlined",
      removable: false,
      className: classnames(s.tagContainer, {
        [s.tagSelected]: isSelected
      }),
      onClick: () => handleToggleKeyword(keyword)
    }, /*#__PURE__*/React.createElement(Text, {
      size: "small"
    }, keyword));
  }))), /*#__PURE__*/React.createElement(Box, {
    direction: "vertical"
  }, /*#__PURE__*/React.createElement(Box, {
    paddingTop: "SP4",
    paddingBottom: "SP4"
  }, /*#__PURE__*/React.createElement(Text, {
    size: "small",
    weight: "bold"
  }, t('core_domain_search.domain_gate.style_header'))), /*#__PURE__*/React.createElement(Box, {
    marginBottom: "SP2"
  }, /*#__PURE__*/React.createElement(Divider, null)), /*#__PURE__*/React.createElement(RadioGroup, {
    value: selectedStyle,
    onChange: value => setStyle(value),
    className: s.radioGroup,
    selectionAreaPadding: "12px 0"
  }, styleOptions.map((_ref4, index) => {
    let id = _ref4.id,
      value = _ref4.value;
    return /*#__PURE__*/React.createElement(RadioGroup.Radio, {
      key: id,
      value: id,
      content: index < styleOptions.length - 1 ? /*#__PURE__*/React.createElement(Box, {
        marginBottom: "SP2"
      }, /*#__PURE__*/React.createElement(Divider, null)) : undefined
    }, value);
  })))))), /*#__PURE__*/React.createElement(FullScreenModalLayout.Footer, null, /*#__PURE__*/React.createElement(Button, {
    fullWidth: true,
    skin: "dark",
    disabled: !hasChanges,
    onClick: handleApply,
    className: s.applyButton
  }, t('core_domain_search.mobile.filters_modal.cta')))));
};
//# sourceMappingURL=MobileFiltersModal.js.map